/**
 * ZyncFit i18n translations — NL (default), EN, DE
 * Usage: import { translations } from '../i18n/translations';
 *        const text = translations[lang].nav.integration;
 *
 * When adding new features, add keys in ALL three languages here.
 */

export const SUPPORTED_LANGUAGES = [
  { value: "nl", label: "Nederlands" },
  { value: "en", label: "English" },
  { value: "de", label: "Deutsch" },
];

export const translations = {
  // ─── DUTCH (default) ──────────────────────────────────
  nl: {
    // Navigation
    nav: {
      appName: "ZyncFit",
      integration: "Integratie",
      products: "Producten & Pasfoto's",
      sessions: "Paskamer sessies",
      analytics: "Conversie & Analytics",
      customers: "Klanten",
      branding: "Branding",
      aiSettings: "AI Instellingen",
      settings: "Instellingen",
      about: "Over",
      poweredBy: "Powered by ZyncFit",
      activeShop: "Actieve winkel",
      logout: "Uitloggen",
      landingPageTemplate: "Landingspagina",
      sizeCharts: "Maattabellen",
      showroom: "Showroom",
    },

    showroomAdmin: {
      title: "Virtuele Showroom",
      subtitle: "Deel deze link of voeg hem toe aan je winkelmenu — klanten zien alle artikelen die virtueel te passen zijn en kunnen er doorheen swipen.",
      linkLabel: "Showroom-link",
      copy: "Kopieer link",
      copied: "Gekopieerd!",
      open: "Showroom bekijken",
      menuTitle: "Toevoegen aan je winkelmenu",
      menuStep1: "Ga in Shopify naar Online Store → Navigation",
      menuStep2: "Kies je hoofdmenu en klik op 'Add menu item'",
      menuStep3: "Plak de showroom-link als bestemming en geef het item een naam, bijv. 'Virtueel passen'",
      productsAvailable: "{count} artikelen beschikbaar in de showroom",
      noProducts: "Nog geen artikelen beschikbaar — upload eerst pasfoto's bij Producten & Pasfoto's.",
      loadFailed: "Kon showroom-voorbeeld niet laden.",
    },

    analytics: {
      title: "Conversie & Analytics",
      subtitle: "Welke paskamer-klanten hebben besteld en wat is er geretourneerd",
      refresh: "Orders vernieuwen",
      refreshing: "Bezig met matchen...",
      moreToCheck: "Nog {count} klanten te controleren — klik nogmaals op 'Orders vernieuwen'.",
      kpiSessions: "Pas-sessies",
      kpiCustomers: "Unieke klanten",
      kpiOrdered: "Besteld",
      kpiOrderedSub: "{count} klanten",
      kpiReturned: "Geretourneerd",
      kpiReturnedSub: "{count} van de bestellers",
      kpiPending: "Nog niet gecheckt",
      colDate: "Datum",
      colCustomer: "Klant",
      colProduct: "Product",
      colInput: "Ingevuld",
      colAdvice: "Maatadvies",
      colOrder: "Bestelling",
      colReturned: "Retour",
      returnedYes: "Geretourneerd",
      returnedNo: "Behouden",
      notChecked: "Nog niet gecheckt",
      yearsShort: "jr",
      aiEstimate: "AI-schatting borst/taille",
      noData: "Nog geen pas-sessies",
      noDataText: "Zodra klanten de paskamer gebruiken verschijnt hier de conversie-data.",
    },

    // Common
    common: {
      save: "Opslaan",
      saving: "Opslaan...",
      cancel: "Annuleren",
      delete: "Verwijderen",
      edit: "Bewerken",
      loading: "Laden...",
      search: "Zoeken",
      all: "Alle",
      yes: "Ja",
      no: "Nee",
      back: "Terug",
      retry: "Opnieuw proberen",
      close: "Sluiten",
      error: "Er ging iets mis",
      success: "Gelukt!",
      poweredBy: "Powered by ZyncFit",
      or: "of",
      status: "Status",
      date: "Datum",
      actions: "Acties",
      email: "E-mailadres",
      name: "Naam",
      password: "Wachtwoord",
    },

    // Settings page
    settings: {
      title: "Instellingen",
      subtitle: "Beheer de taal en algemene instellingen van je ZyncFit app.",
      language: "Taal",
      languageHelp: "Taalinstellingen voor het dashboard en voor klanten.",
      dashboardLanguage: "Dashboard taal",
      dashboardLanguageHelp: "De taal van het admin dashboard (voor jou als beheerder).",
      customerLanguage: "Klanttaal",
      customerLanguageHelp: "De taal voor klantgerichte content: e-mails, widget, paspagina.",
      sizeAdviceTitle: "Maatadvies",
      sizeAdviceDescription: "Bepaal of klanten een maatadvies te zien krijgen bij het passen.",
      sizeAdviceToggle: "Maatadvies tonen aan klanten",
      sizeAdviceToggleHelp: "Als dit uit staat, krijgen klanten alleen de pasfoto te zien — zonder maatadvies of lengte/gewicht-velden.",
      sizeAdviceOffWarning: "Maatadvies staat uit. Klanten zien alleen de virtuele pasfoto. Ook op bestaande paspagina's wordt geen maatadvies meer getoond.",
      saved: "Instellingen opgeslagen!",
      saveFailed: "Opslaan mislukt",
      shopLabel: "Winkel:",
    },

    // Login page
    login: {
      subtitle: "Admin Dashboard",
      emailPlaceholder: "admin@voorbeeld.nl",
      passwordPlaceholder: "Wachtwoord",
      loginButton: "Inloggen",
      loggingIn: "Bezig met inloggen...",
      fieldsRequired: "Vul e-mailadres en wachtwoord in.",
      setupTitle: "Eerste admin account aanmaken",
      namePlaceholder: "Je naam",
      setupPasswordPlaceholder: "Kies een sterk wachtwoord",
      passwordRequirements: "Min. 8 tekens, met hoofdletter, kleine letter en cijfer.",
      createAccount: "Account aanmaken",
      setupWithShopify: "Instellen via Shopify",
      shopifyLoginTitle: "Inloggen via Shopify",
      shopDomain: "Shopify winkel domein",
      continueWithShopify: "Doorgaan met Shopify",
      backToLogin: "Terug naar inloggen",
      loginWithShopify: "Inloggen met Shopify",
      enterShopDomain: "Vul je winkeldomein in.",
      shopifyFailed: "Shopify login mislukt. Probeer het opnieuw.",
    },

    // Integration page
    integration: {
      title: "Integratie",
      shopifyConnection: "Shopify Koppeling",
      connectionDesc: "Verbind je Shopify winkel om producten te importeren en de virtuele paskamer te activeren.",
      connectedShops: "Gekoppelde winkels",
      connectedShopsDesc: "Hieronder zie je al je gekoppelde Shopify winkels. Wissel tussen winkels om hun producten en instellingen te beheren.",
      addShop: "Nieuwe winkel koppelen",
      active: "Actief",
      switchTo: "Selecteren",
      checkingStatus: "Status controleren...",
      connected: "Gekoppeld",
      notConnected: "Niet gekoppeld",
      permissions: "Rechten:",
      connectedOn: "Gekoppeld op:",
      disconnect: "Koppeling verbreken",
      reconnect: "Opnieuw koppelen",
      shopDomainLabel: "Shopify winkel domein",
      shopDomainPlaceholder: "jouwwinkel.myshopify.com",
      shopDomainHelp: "Vul het .myshopify.com domein van je winkel in.",
      installViaAppStore: "Installeer ZyncFit via de Shopify App Store of via de installatielink in je Shopify-admin. Na installatie verschijnt je winkel hier automatisch.",
      installViaAppStoreHelp: "Open de app vanuit je Shopify-admin (Apps → ZyncFit) om de koppeling te starten. Handmatige invoer van je winkeldomein is niet nodig.",
      connectButton: "Koppelen met Shopify",
      connectedSuccess: "Shopify winkel succesvol gekoppeld!",
      enterDomain: "Vul een winkel-domein in",
      confirmDisconnect: "Weet je zeker dat je de koppeling wilt verbreken?",
      disconnected: "Winkel ontkoppeld",
      howItWorks: "Hoe werkt het?",
      step1: "Koppel je Shopify winkel hierboven.",
      step2: "Ga naar Producten & Pasfoto's om kledingfoto's te uploaden.",
      step3: "De Virtueel Passen knop verschijnt automatisch op je productpagina's.",
      step4: "Klanten maken een foto en ontvangen per e-mail hun AI-gegenereerd resultaat.",
      step5: "Bekijk sessies en resultaten in het dashboard.",
      widgetTitle: "Webshop Widget installeren",
      widgetDesc: 'Voeg de "Virtueel Passen" knop toe aan je productpagina\'s. De knop verschijnt alleen bij producten waarvoor je een kledingfoto hebt ingesteld.',
      widgetTip: "De widget zoekt automatisch de juiste plek op de productpagina. Als dit niet goed werkt, gebruik dan het Custom Liquid block hierboven.",
      widgetOption1Title: "Optie 1: Via de Theme Editor (aanbevolen)",
      widgetOption1Step1: "Ga naar Online Store → Themes → Customize",
      widgetOption1Step2: "Open een productpagina in de editor",
      widgetOption1Step3: "Klik op 'Add block' waar je de knop wilt plaatsen",
      widgetOption1Step4: "Kies 'Custom Liquid'",
      widgetOption1Step5: "Plak de onderstaande code:",
      widgetOption1Hint: "Je kunt het blok verslepen naar de gewenste positie op de productpagina.",
      widgetOption2Title: "Optie 2: Automatisch op alle pagina's",
      widgetOption2Desc: "Voeg dit script toe aan Online Store → Themes → Edit code → layout/theme.liquid (vóór </body>):",
      widgetOption2Hint: "Het script detecteert automatisch productpagina's en plaatst de knop onder de 'In winkelwagen' knop.",
      showroomTitle: "Virtuele Showroom — link voor je menu",
      showroomDesc: "Voeg deze link toe aan je winkelnavigatie (Online Store → Navigation). Klanten zien dan alle artikelen die virtueel te passen zijn en kunnen er doorheen swipen.",
      showroomCopy: "Kopieer link",
      showroomCopied: "Gekopieerd!",
    },

    // Products page
    products: {
      title: "Producten & Kledingfoto's",
      description: "Upload een kledingfoto (garment photo) voor elk product. De AI gebruikt deze foto om de klant virtueel het artikel te laten passen.",
      searchPlaceholder: "Zoek producten...",
      sort: "Sorteer",
      type: "Type",
      notConnected: "Shopify winkel niet gekoppeld",
      notConnectedDesc: "Verbind eerst je Shopify winkel via de Integratie pagina.",
      goToIntegration: "Ga naar Integratie",
      noProducts: "Geen producten gevonden",
      noProductsDesc: "Er zijn geen producten die aan je filters voldoen.",
      clearFilters: "Filters wissen",
      ofProducts: "van {total} producten",
      searchResults: "zoekresultaten",
      changePhoto: "Foto wijzigen",
      uploadPhoto: "Foto uploaden",
      landingPage: "Landingspagina",
      deletePhoto: "Foto verwijderen",
      photoSaved: "Kledingfoto opgeslagen voor {name}!",
      confirmDeletePhoto: "Weet je zeker dat je de kledingfoto wilt verwijderen?",
      photoDeleted: "Kledingfoto verwijderd",
      chooseOrUpload: "Kies een bestaande foto of upload een nieuwe",
      sortNewest: "Nieuwste eerst",
      sortOldest: "Oudste eerst",
      sortAZ: "A → Z",
      sortZA: "Z → A",
      sortPriceLow: "Prijs: laag → hoog",
      sortPriceHigh: "Prijs: hoog → laag",
      tabAll: "Alle",
      tabWithPhoto: "Met pasfoto",
      tabWithoutPhoto: "Zonder pasfoto",
      tabActive: "Actief",
      tabDraft: "Concept",
      products: "producten",
    },

    // Sessions page
    sessions: {
      title: "Paskamer sessies",
      description: "Overzicht van alle virtuele paskamer sessies. Klanten die de paskamer hebben gebruikt, verschijnen hier met hun resultaat.",
      noSessions: "Nog geen sessies",
      noSessionsDesc: "Wanneer klanten de virtuele paskamer gebruiken, verschijnen hun sessies hier.",
      emailCol: "E-mailadres",
      productIdCol: "Product ID",
      dateCol: "Datum",
      emailStatus: "E-mail",
      landingPageCol: "Landingspagina",
      sent: "Verzonden",
      notSent: "Niet verzonden",
    },

    // Customers page
    customers: {
      title: "Klanten",
      subtitle: "Beheer klantaccounts, marketingtoestemming en privacy (AVG/GDPR).",
      exportMarketing: "Exporteer marketing lijst",
      exportAll: "Exporteer alles",
      exportFailed: "Export mislukt",
      marketingExported: "Marketing klanten geëxporteerd!",
      allExported: "Alle klanten geëxporteerd!",
      deleted: "Klant verwijderd (AVG recht op vergetelheid).",
      optIn: "Opt-in",
      noConsent: "Geen toestemming",
      accepted: "Geaccepteerd",
      notAccepted: "Niet geaccepteerd",
      marketingOff: "Marketing uit",
      marketingOn: "Marketing aan",
      gdprBanner: "Deze pagina is AVG/GDPR-compliant. Klantgegevens worden verwerkt volgens het privacybeleid. Klanten kunnen via het recht op vergetelheid verwijderd worden.",
      searchPlaceholder: "Zoek op e-mail of naam...",
      filterAll: "Alle",
      filterOptIn: "Marketing opt-in",
      noCustomers: "Nog geen klanten",
      noCustomersDesc: "Wanneer klanten een account aanmaken via de virtuele paskamer, verschijnen ze hier.",
      sessionsCol: "Sessies",
      marketingCol: "Marketing",
      privacyCol: "Privacy",
      registeredCol: "Geregistreerd",
      totalCustomers: "Totaal klanten",
      marketingOptIn: "Marketing opt-in",
      deleteTitle: "Klant verwijderen?",
      deleteConfirm: "Definitief verwijderen",
      deleteWarning: "Dit verwijdert permanent alle gegevens van deze klant inclusief sessies, conform AVG recht op vergetelheid. Dit kan niet ongedaan worden gemaakt.",
      detailsTitle: "Klantdetails",
      noName: "Geen naam",
      notSynced: "Niet gesynchroniseerd",
      csvHeader: "E-mailadres,Naam,Telefoon,Marketing toestemming,Toestemmingsdatum,Geregistreerd",
    },

    // Branding page
    branding: {
      title: "Branding & Huisstijl",
      subtitle: "Pas de look & feel aan van de klantpagina's (landing, dashboard, virtueel passen).",
      saved: "Branding opgeslagen!",
      storeDetails: "Winkelgegevens",
      storeName: "Winkelnaam",
      storeNamePlaceholder: "Jouw Winkel",
      logoUrl: "Logo URL",
      logoHelp: "Link naar je logo (bijv. vanuit Shopify Files)",
      colors: "Kleuren",
      primaryColor: "Primaire kleur",
      secondaryColor: "Secundaire kleur",
      accentColor: "Accent kleur",
      backgroundColor: "Achtergrondkleur",
      textColor: "Tekstkleur",
      style: "Stijl",
      fontFamily: "Lettertype",
      borderRadius: "Hoekafronding",
      buttonStyle: "Button stijl",
      preview: "Preview",
      previewDesc: "Zo ziet de klant jouw paskamer",
      radiusSharp: "Scherp",
      radiusSlight: "Licht",
      radiusRounded: "Afgerond",
      radiusRound: "Rond",
      radiusPill: "Pill",
      previewResult: "Zo staat het artikel bij jou",
      previewButton: "Outfit bestellen",
      previewStoreName: "Jouw Winkel",
      previewSubtitle: "Virtuele Paskamer",
      showImageBadge: "Toon 'Virtueel Passen' label op productfoto",
      showImageBadgeHelp: "Toont een klein label onderaan de productfoto op de productpagina, zodat klanten weten dat virtueel passen beschikbaar is.",
    },

    // Size chart
    sizeChart: {
      title: "Maattabel",
      description: "Voer de maten in centimeters in per maat. De AI gebruikt deze tabel om de juiste maat aan te bevelen.",
      sizeLabel: "Maat",
      addSize: "+ Maat toevoegen",
      saved: "Maattabel opgeslagen!",
      chest: "Borst",
      waist: "Taille",
      hips: "Heupen",
      shoulders: "Schouders",
      armLength: "Armlengte",
      inseam: "Binnenbeenlengte",
      neck: "Nek",
      pageTitle: "Maattabellen per merk",
      pageDescription: "Stel per merk een standaard maattabel in. Alle producten van dat merk erven deze maten automatisch. Je kunt per product nog finetunen via Producten & Pasfoto's.",
      noVendors: "Geen merken gevonden. Producten worden geladen vanuit Shopify.",
      sizes: "maten",
      noChart: "Geen maattabel",
      editChart: "Bewerken",
      addChart: "Maattabel toevoegen",
      vendorDescription: "Deze maten gelden voor alle producten van dit merk, tenzij je per product een eigen maattabel instelt.",
      inheritedFrom: "Deze maten komen van het merk",
      inheritedHint: "Pas ze aan om een eigen maattabel voor dit product op te slaan.",
      scanImage: "📷 Scan afbeelding",
      scanning: "Analyseren...",
      scanSuccess: "AI heeft {count} maten herkend uit de afbeelding. Controleer de waarden en sla op.",
      scanFailed: "Scan mislukt",
      import: "Importeren",
      importButton: "Importeren & converteren",
      scrapeUrl: "🌐 Scrape URL",
      scrapeButton: "Scrape & converteer",
      scraping: "Pagina analyseren...",
      scrapeDescription: "Plak de URL van de size-guide pagina van het merk. De AI leest de maattabellen, rekent inches en UK-maten om naar cm/EU en converteert kledingmaten naar lichaamsmaten. Je krijgt eerst een preview — opslaan doe je zelf.",
      scrapeSuccess: "AI heeft {count} maten van de pagina gehaald. Controleer de waarden en sla op.",
      scrapeFailed: "Scrapen mislukt",
    },

    // AI Settings page
    ai: {
      title: "AI Instellingen",
      subtitle: "Stel de Gemini AI prompts in voor virtueel passen.",
      saved: "Instellingen opgeslagen!",
      loadFailed: "Kan AI-instellingen niet laden",
      saveFailed: "Opslaan mislukt",
      model: "Gemini Model",
      modelDesc: "Model voor stijladvies tekst",
      modelWarning: "**Let op:** Het model voor afbeelding generatie is altijd `gemini-2.0-flash-preview-image-generation`. Het model hierboven wordt alleen gebruikt voor de stijladvies tekst.",
      imagePrompt: "Prompt voor afbeelding generatie",
      imagePromptReset: "Herstel standaard",
      imagePromptDesc: "Deze prompt wordt naar Gemini gestuurd samen met de kledingfoto en klantfoto. Het resultaat is een AI-gegenereerde afbeelding.",
      imagePromptTip: "**Tip:** Gebruik `{garment}` en `{customer}` als placeholders voor de kledingfoto en klantfoto in de prompt.",
      stylePrompt: "Prompt voor stijladvies (tekst)",
      stylePromptReset: "Herstel standaard",
      stylePromptDesc: "Deze prompt genereert het tekstuele stijladvies dat bij het resultaat wordt getoond.",
      recommended: "aanbevolen",
      newest: "nieuwste",
      premium: "premium",
      textOnly: "alleen stijladvies tekst",
    },

    // Landing page editor
    landingEditor: {
      title: "Landingspagina bewerken — {name}",
      backAction: "Producten",
      saving: "Opslaan...",
      saved: "Landingspagina opgeslagen!",
      titleSection: "Paginatitel & introductietekst",
      pageTitle: "Paginatitel",
      pageTitlePlaceholder: "Jouw virtuele look",
      introText: "Introductietekst",
      introPlaceholder: "Ontdek hoe dit artikel bij jou staat...",
      sizeSection: "Maatadvies & pasvormtekst",
      showCalculator: "Maatcalculator weergeven op de landingspagina",
      sizeGuide: "Pasvormadvies tekst",
      sizeGuidePlaceholder: "Informatie over maten en pasvorm...",
      extraSection: "Extra tekst & afbeeldingen",
      additionalText: "Extra tekst",
      additionalTextPlaceholder: "Vrije tekst voor extra informatie...",
      extraImages: "Extra afbeeldingen",
      addImage: "Afbeelding toevoegen",
      dropImage: "of sleep hier naartoe",
      orderSection: "Bestelknop",
      orderButtonText: "Knoptekst",
      orderButtonTextPlaceholder: "Bestelling afronden",
      orderButtonUrl: "Knop-URL (optioneel)",
      orderButtonUrlHelp: "Als leeg, wordt de product-URL uit Shopify gebruikt.",
    },

    // Landing page template (global)
    landingTemplate: {
      title: "Landingspagina template",
      saved: "Landingspagina template opgeslagen!",
      infoBanner: "Dit is de standaard landingspagina die voor alle producten wordt gebruikt. Als je bij een specifiek product een landingspagina instelt, wordt die in plaats hiervan gebruikt.",
      titleSection: "Paginatitel & introductietekst",
      pageTitle: "Paginatitel",
      pageTitlePlaceholder: "Jouw virtuele look",
      pageTitleHelp: "Wordt bovenaan elke landingspagina weergegeven.",
      introText: "Introductietekst",
      introPlaceholder: "Ontdek hoe dit artikel bij jou staat...",
      introHelp: "Wordt onder de foto's weergegeven op alle landingspagina's.",
      sizeSection: "Maatadvies & pasvormtekst",
      showCalculator: "Maatcalculator weergeven op de landingspagina",
      sizeGuide: "Pasvormadvies tekst",
      sizeGuidePlaceholder: "Informatie over maten en pasvorm...",
      extraSection: "Extra tekst & afbeeldingen",
      additionalText: "Extra tekst",
      additionalTextPlaceholder: "Vrije tekst voor extra informatie...",
      extraImages: "Extra afbeeldingen",
      addImage: "Afbeelding toevoegen",
      dropImage: "of sleep hier naartoe",
      orderSection: "Bestelknop",
      orderButtonText: "Knoptekst",
      orderButtonTextPlaceholder: "Bestelling afronden",
      orderButtonUrl: "Knop-URL (optioneel)",
      orderButtonUrlHelp: "Als leeg, wordt de product-URL uit Shopify gebruikt.",
    },

    // Try-on page (public)
    tryOn: {
      title: "Virtueel Passen",
      invalidLink: "Ongeldige link.",
      loadFailed: "Kan product niet laden",
      cameraNotAvailable: "Camera niet beschikbaar. Gebruik 'Upload een foto' hieronder.",
      takePhotoFirst: "Maak eerst een foto van jezelf.",
      enterEmail: "Vul je e-mailadres in.",
      photoTooBig: "De foto is te groot. Probeer een kleinere foto te uploaden.",
      serverUnreachable: "Kan de server niet bereiken. Controleer je internetverbinding en probeer het opnieuw.",
      loading: "Laden...",
      success: "Gelukt!",
      resultSent: "Je virtuele pas-resultaat is onderweg naar",
      styleAdvice: "Stijladvies",
      imageTemporarilyUnavailable: "De AI-afbeelding kon momenteel niet worden gegenereerd door hoge servervraag. Je stijladvies is wel beschikbaar. Het resultaat is ook per e-mail verstuurd.",
      emailFailed: "De e-mail kon niet worden verzonden. Gebruik de link hieronder om je resultaat te bekijken.",
      openLandingDirect: "Bekijk je resultaat →",
      backToShop: "Terug naar winkel",
      thisArticle: "Dit artikel",
      yourPhoto: "Jouw foto",
      chooseOtherPhoto: "Andere foto kiezen",
      takePhotoLabel: "Maak een foto van jezelf",
      takePhotoHelp: "Een full body foto in aansluitende kleding zorgt voor het beste maatadvies.",
      photoTip1: "Ga rechtop staan met je armen licht van je lichaam af",
      photoTip2: "Zorg dat je hele lichaam in beeld is (hoofd tot voeten)",
      photoTip3: "Laat iemand anders de foto nemen op ± 2 meter afstand",
      photoTip4: "Draag aansluitende kleding (geen dikke jas of trui)",
      photoTip5: "Zorg voor goede belichting en een rustige achtergrond",
      photoTip6: "Sta met je voeten op schouderbreedte uit elkaar",
      openCamera: "Camera openen",
      cameraGuideSelf: "Lijn je lichaam uit met het silhouet — hoofd tot voeten zichtbaar",
      cameraGuideOther: "Richt op de hele persoon — hoofd tot voeten in het silhouet",
      distanceTip: "Vul het silhouet — ca. 2 meter afstand",
      guideShoulders: "schouders",
      guideChest: "borst",
      guideWaist: "taille",
      guideHips: "heupen",
      uploadPhoto: "Upload een foto",
      emailLabel: "E-mailadres",
      emailPlaceholder: "jouw@email.nl",
      emailHelp: "We sturen het resultaat naar dit adres",
      bodyInfoLabel: "Jouw gegevens",
      bodyInfoHelp: "Vul je lengte, gewicht en leeftijd in voor een nauwkeurigere maatberekening.",
      heightLabel: "Lengte (cm)",
      weightLabel: "Gewicht (kg)",
      ageLabel: "Leeftijd",
      processing: "Bezig met verwerken...",
      submit: "Virtueel passen",
      cancelCamera: "Annuleer",
      timerOff: "Uit",
      step1: "Foto wordt geanalyseerd...",
      step2: "Kledingstuk wordt op je gepast...",
      step3: "Lichaamsmaten worden berekend...",
      step4: "Beste maat wordt bepaald...",
      step5: "Resultaat wordt klaargezet...",
      heightWeightRequired: "Vul je lengte en gewicht in — die zijn nodig voor een betrouwbaar maatadvies.",
      fitPreferenceLabel: "Pasvorm-voorkeur",
      fitSlim: "Getailleerd",
      fitRegular: "Normaal",
      fitComfort: "Ruim",
      sidePhotoLabel: "Zijaanzicht-foto (optioneel)",
      sidePhotoHelp: "Een extra foto van opzij maakt de schatting van borst- en tailleomvang veel nauwkeuriger.",
      addSidePhotoCamera: "Zijfoto maken",
      addSidePhotoUpload: "Zijfoto uploaden",
      removeSidePhoto: "Zijfoto verwijderen",
      cameraGuideSide: "Draai een kwartslag — sta met je zij naar de camera",
      yourSizes: "Jouw maatadvies",
      alternativeSizeHint: "Twijfelgeval — maat {alt} kan ook passen",
      privacyConsent: "Ik geef toestemming om mijn foto en gegevens te verwerken voor het virtueel passen. Foto's worden automatisch verwijderd.",
      privacyPolicyLink: "Privacyverklaring",
      privacyConsentRequired: "Geef eerst toestemming voor het verwerken van je foto.",
      fitBuildAthletic: "Atletische bouw gedetecteerd (borst-taille verschil {drop} cm)",
      fitBuildFuller: "Vollere taille gedetecteerd (borst-taille verschil {drop} cm)",
      fitTipAthleticJacket: "Bij jouw bouw past maat {size} op de schouders; een kleermaker kan de taille eenvoudig innemen (±15-25 per stuk). Maat {alt} zit ruimer maar slobbert bij de taille.",
      fitTipAthleticJacketOption: "Maat {size} volgt de maattabel. Valt hij bij jouw atletische bouw te ruim rond de taille, dan is {alt} + de taille laten innemen een uitstekende optie — schouders zijn leidend.",
      fitTipFullerJacket: "Maat {size} geeft comfort bij de taille. Zit de schouder te ruim, dan is {alt} + taille laten uitleggen meestal niet mogelijk — kies liever ruim.",
      fitTipBetweenJacket: "Je zit tussen twee maten in. {size} = getailleerd silhouet, {alt} = meer bewegingsruimte. Schouders zijn leidend — die kan een kleermaker niet aanpassen.",
      fitTipBetweenTrousers: "Tussen twee maten? Kies {size} — een taille kan tot ±4 cm ingenomen of uitgelegd worden door een kleermaker.",
      fitTipTrousersAlter: "Tip: de taille van een pantalon kan een kleermaker tot ±4 cm aanpassen; de lengte vrijwel altijd.",
      fitTipBetweenGilet: "Gilet {size} sluit aan; {alt} valt losser. Een gilet hoort aansluitend te zitten — achterop zit een gesp voor fijnafstelling.",
      adjustedByHistory: "Op basis van je eerdere aankopen of ruilingen bij dit merk",
      adjustedByBrandUp: "Dit merk valt klein — we adviseren een maat groter",
      adjustedByBrandDown: "Dit merk valt groot — we adviseren een maat kleiner",
      adjustedByFit: "Aangepast aan je pasvorm-voorkeur",
    },

    // Standalone showroom page
    showroom: {
      title: "Virtuele Showroom",
      subtitle: "Swipe door de collectie en kies wat je virtueel wilt passen",
      tryThis: "Dit pak passen",
      invalidLink: "Ongeldige link.",
      loadFailed: "Kan de showroom niet laden. Probeer het later opnieuw.",
      empty: "Er zijn nog geen artikelen beschikbaar om virtueel te passen.",
      viewSwipe: "Swipen",
      viewGrid: "Overzicht",
      itemCount: "{count} artikelen",
      viewProduct: "Bekijk in winkel",
    },

    // Customer landing page
    landing: {
      invalidLink: "Ongeldige link.",
      notFound: "Deze pagina is niet gevonden of verlopen.",
      pageNotFound: "Pagina niet gevonden",
      calcFailed: "Kon maatadvies niet berekenen.",
      defaultTitle: "Jouw virtuele look – {product}",
      defaultIntro: "Ontdek hieronder hoe dit artikel bij jou staat, speciaal samengesteld door AI.",
      yourLook: "Jouw look",
      styleAdvice: "Stijladvies",
      originalPhotos: "Originele foto's",
      yourPhotoAndArticle: "Jouw foto & het artikel",
      yourPhoto: "Jouw foto",
      sizeAdvice: "Maatadvies",
      sizeCalculator: "Maatcalculator",
      sizeCalcHelp: "Vul je gegevens in voor een persoonlijk maatadvies.",
      age: "Leeftijd",
      heightCm: "Lengte (cm)",
      weightKg: "Gewicht (kg)",
      calculating: "Berekenen...",
      calculateMySize: "Bereken mijn maat",
      sizeResult: "Maat {size}",
      extraPhotos: "Extra foto's",
      orderOutfit: "Outfit bestellen",
      dashboardPromo: "Jouw virtuele paskamer",
      dashboardPromoText: "Maak een gratis account aan om al je virtuele pas-sessies op te slaan en terug te bekijken.",
      viewOutfits: "Bekijk mijn outfits →",
      recommendedSize: "Aanbevolen maat",
      recommendedSizes: "Aanbevolen maten",
      colbert: "Colbert",
      gilet: "Gilet",
      pantalon: "Pantalon",
      overhemd: "Overhemd",
      overall: "Maat",
      bodyMeasurements: "Jouw geschatte lichaamsafmetingen",
      chest: "Borst",
      waist: "Taille",
      hips: "Heupen",
      shoulders: "Schouders",
      armLength: "Armlengte",
      inseam: "Binnenbeenlengte",
      neck: "Nek",
      cm: "cm",
      confidence: "Betrouwbaarheid",
      confidenceLow: "laag",
      confidenceMedium: "gemiddeld",
      confidenceHigh: "hoog",
    },

    // Customer session detail page
    sessionDetail: {
      backToOverview: "← Terug naar overzicht",
      notFound: "Niet gevonden",
      notFoundDesc: "Deze sessie bestaat niet.",
      yourLook: "Jouw look",
      styleAdvice: "Stijladvies",
      originalPhotos: "Originele foto's",
      yourPhotoAndArticle: "Jouw foto & het artikel",
      orderOutfit: "Outfit bestellen",
    },

    // Customer dashboard / auth
    dashboard: {
      virtualFittingRoom: "Jouw virtuele paskamer",
      welcomeBack: "Welkom terug",
      createAccount: "Account aanmaken",
      namePlaceholder: "Naam (optioneel)",
      emailPlaceholder: "E-mailadres",
      passwordPlaceholder: "Wachtwoord",
      privacyLabel: "Ik ga akkoord met het <strong>privacybeleid</strong> en begrijp hoe mijn gegevens worden verwerkt.",
      privacyRequired: "*",
      marketingLabel: "Ja, ik wil graag nieuws en aanbiedingen ontvangen per e-mail (optioneel).",
      privacyError: "Je moet het privacybeleid accepteren om een account aan te maken.",
      pleaseWait: "Even geduld...",
      login: "Inloggen",
      noAccount: "Nog geen account? Registreer hier →",
      hasAccount: "← Al een account? Log in",
      logout: "Uitloggen",
      myFittingRoom: "Mijn Virtuele Paskamer",
      outfitsGenerated: "{count} outfit(s) gegenereerd",
      viewOutfits: "Bekijk al je gegenereerde outfits",
      noOutfits: "Nog geen outfits",
      noOutfitsDesc: "Gebruik de virtuele paskamer op een webshop om je eerste outfit te genereren.",
      noImage: "Geen afbeelding",
      unknownProduct: "Onbekend product",
      aiGenerated: "AI gegenereerd",
    },

    // Backend messages (used server-side, returned to client)
    api: {
      emailPasswordRequired: "E-mailadres en wachtwoord zijn verplicht.",
      passwordMinLength: "Wachtwoord moet minimaal 6 tekens zijn.",
      privacyRequired: "Je moet het privacybeleid accepteren om een account aan te maken.",
      emailExists: "Er bestaat al een account met dit e-mailadres.",
      registerFailed: "Er ging iets mis bij het registreren.",
      invalidCredentials: "Ongeldige inloggegevens.",
      loginFailed: "Er ging iets mis bij het inloggen.",
      accountNotFound: "Account niet gevonden.",
      sessionNotFound: "Sessie niet gevonden.",
      genericError: "Er ging iets mis.",
      unknownProduct: "Onbekend product",
      shopNotConnected: "Winkel niet gekoppeld. Ga naar Integratie om te verbinden met Shopify.",
      tokenExpired: "Toegangstoken verlopen. Koppel de winkel opnieuw.",
      cannotFetchProducts: "Kan producten niet ophalen van Shopify",
      shopAndProductRequired: "shop en productId zijn verplicht",
      tryOnNotAvailable: "Virtueel passen is niet beschikbaar voor dit product.",
      allFieldsRequired: "shop, productId, customerEmail en customerPhotoB64 zijn verplicht",
      photoTooLarge: "De foto is te groot. Probeer een kleinere foto of neem een nieuwe foto.",
      invalidEmail: "Ongeldig e-mailadres",
      processingError: "Er ging iets mis bij het verwerken",
      customerNotFound: "Klant niet gevonden.",
      cannotFetchCustomers: "Kon klanten niet ophalen.",
      cannotFetchCustomer: "Kon klant niet ophalen.",
      cannotDeleteCustomer: "Kon klant niet verwijderen.",
      cannotExportCustomers: "Kon klanten niet exporteren.",
      cannotToggleConsent: "Kon toestemming niet wijzigen.",
      cannotFetchBranding: "Kon branding niet ophalen.",
      cannotSaveBranding: "Kon branding niet opslaan.",
      defaultOrderButton: "Bestelling afronden",
      sizeAdvice: "Op basis van uw maten adviseren wij maat {size}.",
    },

    // Email templates
    emailTemplate: {
      subject: "Uw virtuele paskamer – {product}",
      heading: "Uw virtuele paskamer is klaar!",
      greeting: "Beste klant,",
      body: "Hieronder vindt u het resultaat van uw virtuele paskamer sessie.",
      viewResult: "Bekijk uw resultaat",
      sendFailed: "Email verzenden mislukt",
    },
  },

  // ─── ENGLISH ──────────────────────────────────
  en: {
    nav: {
      appName: "ZyncFit",
      integration: "Integration",
      products: "Products & Garment Photos",
      sessions: "Fitting Sessions",
      analytics: "Conversion & Analytics",
      customers: "Customers",
      branding: "Branding",
      aiSettings: "AI Settings",
      settings: "Settings",
      about: "About",
      poweredBy: "Powered by ZyncFit",
      activeShop: "Active shop",
      logout: "Log out",
      landingPageTemplate: "Landing Page",
      sizeCharts: "Size Charts",
      showroom: "Showroom",
    },

    showroomAdmin: {
      title: "Virtual Showroom",
      subtitle: "Share this link or add it to your store menu — customers see all items available for virtual try-on and can swipe through them.",
      linkLabel: "Showroom link",
      copy: "Copy link",
      copied: "Copied!",
      open: "View showroom",
      menuTitle: "Add to your store menu",
      menuStep1: "In Shopify, go to Online Store → Navigation",
      menuStep2: "Choose your main menu and click 'Add menu item'",
      menuStep3: "Paste the showroom link as the destination and name the item, e.g. 'Virtual try-on'",
      productsAvailable: "{count} items available in the showroom",
      noProducts: "No items available yet — upload garment photos under Products & Fitting Photos first.",
      loadFailed: "Could not load showroom preview.",
    },

    analytics: {
      title: "Conversion & Analytics",
      subtitle: "Which fitting-room customers ordered and what was returned",
      refresh: "Refresh orders",
      refreshing: "Matching...",
      moreToCheck: "{count} more customers to check — click 'Refresh orders' again.",
      kpiSessions: "Fitting sessions",
      kpiCustomers: "Unique customers",
      kpiOrdered: "Ordered",
      kpiOrderedSub: "{count} customers",
      kpiReturned: "Returned",
      kpiReturnedSub: "{count} of buyers",
      kpiPending: "Not yet checked",
      colDate: "Date",
      colCustomer: "Customer",
      colProduct: "Product",
      colInput: "Entered",
      colAdvice: "Size advice",
      colOrder: "Order",
      colReturned: "Return",
      returnedYes: "Returned",
      returnedNo: "Kept",
      notChecked: "Not checked yet",
      yearsShort: "y",
      aiEstimate: "AI estimate chest/waist",
      noData: "No fitting sessions yet",
      noDataText: "Once customers use the fitting room, conversion data appears here.",
    },

    common: {
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      loading: "Loading...",
      search: "Search",
      all: "All",
      yes: "Yes",
      no: "No",
      back: "Back",
      retry: "Try again",
      close: "Close",
      error: "Something went wrong",
      success: "Success!",
      poweredBy: "Powered by ZyncFit",
      or: "or",
      status: "Status",
      date: "Date",
      actions: "Actions",
      email: "Email address",
      name: "Name",
      password: "Password",
    },

    settings: {
      title: "Settings",
      subtitle: "Manage the language and general settings of your ZyncFit app.",
      language: "Language",
      languageHelp: "Language settings for the dashboard and for customers.",
      dashboardLanguage: "Dashboard language",
      dashboardLanguageHelp: "The language of the admin dashboard (for you as the manager).",
      customerLanguage: "Customer language",
      customerLanguageHelp: "The language for customer-facing content: emails, widget, fitting page.",
      sizeAdviceTitle: "Size advice",
      sizeAdviceDescription: "Control whether customers see a size recommendation when trying on.",
      sizeAdviceToggle: "Show size advice to customers",
      sizeAdviceToggleHelp: "When off, customers only see the try-on photo — without size advice or height/weight fields.",
      sizeAdviceOffWarning: "Size advice is off. Customers only see the virtual try-on photo. Existing fitting pages also stop showing size advice.",
      saved: "Settings saved!",
      saveFailed: "Save failed",
      shopLabel: "Shop:",
    },

    login: {
      subtitle: "Admin Dashboard",
      emailPlaceholder: "admin@example.com",
      passwordPlaceholder: "Password",
      loginButton: "Log in",
      loggingIn: "Logging in...",
      fieldsRequired: "Please enter email and password.",
      setupTitle: "Create first admin account",
      namePlaceholder: "Your name",
      setupPasswordPlaceholder: "Choose a strong password",
      passwordRequirements: "Min. 8 characters, with uppercase, lowercase and number.",
      createAccount: "Create account",
      setupWithShopify: "Set up with Shopify",
      shopifyLoginTitle: "Log in with Shopify",
      shopDomain: "Shopify store domain",
      continueWithShopify: "Continue with Shopify",
      backToLogin: "Back to login",
      loginWithShopify: "Log in with Shopify",
      enterShopDomain: "Please enter your store domain.",
      shopifyFailed: "Shopify login failed. Please try again.",
    },

    integration: {
      title: "Integration",
      shopifyConnection: "Shopify Connection",
      connectionDesc: "Connect your Shopify store to import products and activate the virtual fitting room.",
      connectedShops: "Connected shops",
      connectedShopsDesc: "Below you can see all your connected Shopify shops. Switch between shops to manage their products and settings.",
      addShop: "Connect new shop",
      active: "Active",
      switchTo: "Select",
      checkingStatus: "Checking status...",
      connected: "Connected",
      notConnected: "Not connected",
      permissions: "Permissions:",
      connectedOn: "Connected on:",
      disconnect: "Disconnect",
      reconnect: "Reconnect",
      shopDomainLabel: "Shopify store domain",
      shopDomainPlaceholder: "yourstore.myshopify.com",
      shopDomainHelp: "Enter the .myshopify.com domain of your store.",
      installViaAppStore: "Install ZyncFit via the Shopify App Store or via the install link in your Shopify admin. After installation your store appears here automatically.",
      installViaAppStoreHelp: "Open the app from your Shopify admin (Apps → ZyncFit) to start the connection. No manual store domain entry needed.",
      connectButton: "Connect with Shopify",
      connectedSuccess: "Shopify store connected successfully!",
      enterDomain: "Please enter a store domain",
      confirmDisconnect: "Are you sure you want to disconnect?",
      disconnected: "Store disconnected",
      howItWorks: "How does it work?",
      step1: "Connect your Shopify store above.",
      step2: "Go to Products & Garment Photos to upload garment photos.",
      step3: "The Virtual Try-On button automatically appears on your product pages.",
      step4: "Customers take a photo and receive their AI-generated result by email.",
      step5: "View sessions and results in the dashboard.",
      widgetTitle: "Install webshop widget",
      widgetDesc: 'Add the "Virtual Try-On" button to your product pages. The button only appears on products for which you have set a garment photo.',
      widgetTip: "The widget automatically finds the right position on the product page. If this doesn't work well, use the Custom Liquid block above.",
      widgetOption1Title: "Option 1: Via the Theme Editor (recommended)",
      widgetOption1Step1: "Go to Online Store → Themes → Customize",
      widgetOption1Step2: "Open a product page in the editor",
      widgetOption1Step3: "Click 'Add block' where you want the button",
      widgetOption1Step4: "Choose 'Custom Liquid'",
      widgetOption1Step5: "Paste the code below:",
      widgetOption1Hint: "You can drag the block to the desired position on the product page.",
      widgetOption2Title: "Option 2: Automatically on all pages",
      widgetOption2Desc: "Add this script to Online Store → Themes → Edit code → layout/theme.liquid (before </body>):",
      widgetOption2Hint: "The script automatically detects product pages and places the button below the 'Add to cart' button.",
      showroomTitle: "Virtual Showroom — link for your menu",
      showroomDesc: "Add this link to your store navigation (Online Store → Navigation). Customers will see all items available for virtual try-on and can swipe through them.",
      showroomCopy: "Copy link",
      showroomCopied: "Copied!",
    },

    products: {
      title: "Products & Garment Photos",
      description: "Upload a garment photo for each product. The AI uses this photo to let the customer virtually try on the item.",
      searchPlaceholder: "Search products...",
      sort: "Sort",
      type: "Type",
      notConnected: "Shopify store not connected",
      notConnectedDesc: "First connect your Shopify store via the Integration page.",
      goToIntegration: "Go to Integration",
      noProducts: "No products found",
      noProductsDesc: "No products match your filters.",
      clearFilters: "Clear filters",
      ofProducts: "of {total} products",
      searchResults: "search results",
      changePhoto: "Change photo",
      uploadPhoto: "Upload photo",
      landingPage: "Landing page",
      deletePhoto: "Delete photo",
      photoSaved: "Garment photo saved for {name}!",
      confirmDeletePhoto: "Are you sure you want to delete the garment photo?",
      photoDeleted: "Garment photo deleted",
      chooseOrUpload: "Choose an existing photo or upload a new one",
      sortNewest: "Newest first",
      sortOldest: "Oldest first",
      sortAZ: "A → Z",
      sortZA: "Z → A",
      sortPriceLow: "Price: low → high",
      sortPriceHigh: "Price: high → low",
      tabAll: "All",
      tabWithPhoto: "With photo",
      tabWithoutPhoto: "Without photo",
      tabActive: "Active",
      tabDraft: "Draft",
      products: "products",
    },

    sessions: {
      title: "Fitting Sessions",
      description: "Overview of all virtual fitting room sessions. Customers who used the fitting room appear here with their result.",
      noSessions: "No sessions yet",
      noSessionsDesc: "When customers use the virtual fitting room, their sessions appear here.",
      emailCol: "Email address",
      productIdCol: "Product ID",
      dateCol: "Date",
      emailStatus: "Email",
      landingPageCol: "Landing page",
      sent: "Sent",
      notSent: "Not sent",
    },

    customers: {
      title: "Customers",
      subtitle: "Manage customer accounts, marketing consent, and privacy (GDPR).",
      exportMarketing: "Export marketing list",
      exportAll: "Export all",
      exportFailed: "Export failed",
      marketingExported: "Marketing customers exported!",
      allExported: "All customers exported!",
      deleted: "Customer deleted (GDPR right to erasure).",
      optIn: "Opt-in",
      noConsent: "No consent",
      accepted: "Accepted",
      notAccepted: "Not accepted",
      marketingOff: "Marketing off",
      marketingOn: "Marketing on",
      gdprBanner: "This page is GDPR compliant. Customer data is processed according to the privacy policy. Customers can be deleted via the right to erasure.",
      searchPlaceholder: "Search by email or name...",
      filterAll: "All",
      filterOptIn: "Marketing opt-in",
      noCustomers: "No customers yet",
      noCustomersDesc: "When customers create an account via the virtual fitting room, they appear here.",
      sessionsCol: "Sessions",
      marketingCol: "Marketing",
      privacyCol: "Privacy",
      registeredCol: "Registered",
      totalCustomers: "Total customers",
      marketingOptIn: "Marketing opt-in",
      deleteTitle: "Delete customer?",
      deleteConfirm: "Permanently delete",
      deleteWarning: "This permanently deletes all data for this customer including sessions, in accordance with GDPR right to erasure. This cannot be undone.",
      detailsTitle: "Customer details",
      noName: "No name",
      notSynced: "Not synced",
      csvHeader: "Email,Name,Phone,Marketing consent,Consent date,Registered",
    },

    branding: {
      title: "Branding & Style",
      subtitle: "Customize the look & feel of the customer pages (landing, dashboard, virtual try-on).",
      saved: "Branding saved!",
      storeDetails: "Store details",
      storeName: "Store name",
      storeNamePlaceholder: "Your Store",
      logoUrl: "Logo URL",
      logoHelp: "Link to your logo (e.g. from Shopify Files)",
      colors: "Colors",
      primaryColor: "Primary color",
      secondaryColor: "Secondary color",
      accentColor: "Accent color",
      backgroundColor: "Background color",
      textColor: "Text color",
      style: "Style",
      fontFamily: "Font family",
      borderRadius: "Border radius",
      buttonStyle: "Button style",
      preview: "Preview",
      previewDesc: "How the customer sees your fitting room",
      radiusSharp: "Sharp",
      radiusSlight: "Slight",
      radiusRounded: "Rounded",
      radiusRound: "Round",
      radiusPill: "Pill",
      previewResult: "This is how the item looks on you",
      previewButton: "Order outfit",
      previewStoreName: "Your Store",
      previewSubtitle: "Virtual Fitting Room",
      showImageBadge: "Show 'Virtual Try-On' label on product image",
      showImageBadgeHelp: "Shows a small label at the bottom of the product image on product pages, letting customers know virtual try-on is available.",
    },

    sizeChart: {
      title: "Size Chart",
      description: "Enter the measurements in centimeters per size. The AI uses this chart to recommend the right size.",
      sizeLabel: "Size",
      addSize: "+ Add size",
      saved: "Size chart saved!",
      chest: "Chest",
      waist: "Waist",
      hips: "Hips",
      shoulders: "Shoulders",
      armLength: "Arm length",
      inseam: "Inseam",
      neck: "Neck",
      pageTitle: "Size Charts by Brand",
      pageDescription: "Set a default size chart per brand. All products from that brand automatically inherit these measurements. You can fine-tune per product via Products & Garment Photos.",
      noVendors: "No brands found. Products are loaded from Shopify.",
      sizes: "sizes",
      noChart: "No size chart",
      editChart: "Edit",
      addChart: "Add size chart",
      vendorDescription: "These measurements apply to all products of this brand, unless you set a custom size chart per product.",
      inheritedFrom: "These measurements come from the brand",
      inheritedHint: "Modify them to save a custom size chart for this product.",
      scanImage: "📷 Scan image",
      scanning: "Analyzing...",
      scanSuccess: "AI detected {count} sizes from the image. Review the values and save.",
      scanFailed: "Scan failed",
      import: "Import",
      importButton: "Import & convert",
      scrapeUrl: "🌐 Scrape URL",
      scrapeButton: "Scrape & convert",
      scraping: "Analyzing page...",
      scrapeDescription: "Paste the URL of the brand's size guide page. The AI reads the size charts, converts inches and UK sizes to cm/EU, and converts garment measurements to body measurements. You'll get a preview first — you save it yourself.",
      scrapeSuccess: "AI extracted {count} sizes from the page. Review the values and save.",
      scrapeFailed: "Scrape failed",
    },

    ai: {
      title: "AI Settings",
      subtitle: "Configure the Gemini AI prompts for virtual try-on.",
      saved: "Settings saved!",
      loadFailed: "Could not load AI settings",
      saveFailed: "Save failed",
      model: "Gemini Model",
      modelDesc: "Model for style advice text",
      modelWarning: "**Note:** The model for image generation is always `gemini-2.0-flash-preview-image-generation`. The model above is only used for the style advice text.",
      imagePrompt: "Image generation prompt",
      imagePromptReset: "Reset to default",
      imagePromptDesc: "This prompt is sent to Gemini along with the garment photo and customer photo. The result is an AI-generated image.",
      imagePromptTip: "**Tip:** Use `{garment}` and `{customer}` as placeholders for the garment photo and customer photo in the prompt.",
      stylePrompt: "Style advice prompt (text)",
      stylePromptReset: "Reset to default",
      stylePromptDesc: "This prompt generates the textual style advice shown with the result.",
      recommended: "recommended",
      newest: "newest",
      premium: "premium",
      textOnly: "style advice text only",
    },

    landingEditor: {
      title: "Edit landing page — {name}",
      backAction: "Products",
      saving: "Saving...",
      saved: "Landing page saved!",
      titleSection: "Page title & introduction",
      pageTitle: "Page title",
      pageTitlePlaceholder: "Your virtual look",
      introText: "Introduction text",
      introPlaceholder: "Discover how this item looks on you...",
      sizeSection: "Size advice & fit text",
      showCalculator: "Show size calculator on the landing page",
      sizeGuide: "Fit advice text",
      sizeGuidePlaceholder: "Information about sizes and fit...",
      extraSection: "Extra text & images",
      additionalText: "Extra text",
      additionalTextPlaceholder: "Free text for additional information...",
      extraImages: "Extra images",
      addImage: "Add image",
      dropImage: "or drag and drop here",
      orderSection: "Order button",
      orderButtonText: "Button text",
      orderButtonTextPlaceholder: "Complete order",
      orderButtonUrl: "Button URL (optional)",
      orderButtonUrlHelp: "If empty, the Shopify product URL will be used.",
    },

    landingTemplate: {
      title: "Landing page template",
      saved: "Landing page template saved!",
      infoBanner: "This is the default landing page used for all products. If you set a landing page on a specific product, that one will be used instead.",
      titleSection: "Page title & introduction",
      pageTitle: "Page title",
      pageTitlePlaceholder: "Your virtual look",
      pageTitleHelp: "Displayed at the top of every landing page.",
      introText: "Introduction text",
      introPlaceholder: "Discover how this item looks on you...",
      introHelp: "Displayed below the photos on all landing pages.",
      sizeSection: "Size advice & fit text",
      showCalculator: "Show size calculator on the landing page",
      sizeGuide: "Fit advice text",
      sizeGuidePlaceholder: "Information about sizes and fit...",
      extraSection: "Extra text & images",
      additionalText: "Extra text",
      additionalTextPlaceholder: "Free text for additional information...",
      extraImages: "Extra images",
      addImage: "Add image",
      dropImage: "or drag and drop here",
      orderSection: "Order button",
      orderButtonText: "Button text",
      orderButtonTextPlaceholder: "Complete order",
      orderButtonUrl: "Button URL (optional)",
      orderButtonUrlHelp: "If empty, the Shopify product URL will be used.",
    },

    tryOn: {
      title: "Virtual Try-On",
      invalidLink: "Invalid link.",
      loadFailed: "Could not load product",
      cameraNotAvailable: "Camera not available. Use 'Upload a photo' below.",
      takePhotoFirst: "Please take a photo of yourself first.",
      enterEmail: "Please enter your email address.",
      photoTooBig: "The photo is too large. Try uploading a smaller photo.",
      serverUnreachable: "Cannot reach the server. Check your internet connection and try again.",
      loading: "Loading...",
      success: "Success!",
      resultSent: "Your virtual try-on result is on its way to",
      styleAdvice: "Style advice",
      imageTemporarilyUnavailable: "The AI image could not be generated due to high server demand. Your style advice is available below. The result has also been sent by email.",
      emailFailed: "The email could not be sent. Use the link below to view your result.",
      openLandingDirect: "View your result →",
      backToShop: "Back to shop",
      thisArticle: "This item",
      yourPhoto: "Your photo",
      chooseOtherPhoto: "Choose another photo",
      takePhotoLabel: "Take a photo of yourself",
      takePhotoHelp: "A full body photo in fitted clothing gives the best size advice.",
      photoTip1: "Stand upright with your arms slightly away from your body",
      photoTip2: "Make sure your full body is visible (head to toe)",
      photoTip3: "Have someone else take the photo from about 2 meters away",
      photoTip4: "Wear fitted clothing (no thick coats or sweaters)",
      photoTip5: "Ensure good lighting and a plain background",
      photoTip6: "Stand with your feet shoulder-width apart",
      openCamera: "Open camera",
      cameraGuideSelf: "Align your body with the silhouette — head to toe visible",
      cameraGuideOther: "Aim at the full person — head to toe in the silhouette",
      distanceTip: "Fill the silhouette — approx. 2 meters distance",
      guideShoulders: "shoulders",
      guideChest: "chest",
      guideWaist: "waist",
      guideHips: "hips",
      uploadPhoto: "Upload a photo",
      emailLabel: "Email address",
      emailPlaceholder: "your@email.com",
      emailHelp: "We'll send the result to this address",
      bodyInfoLabel: "Your details",
      bodyInfoHelp: "Enter your height, weight and age for more accurate size calculation.",
      heightLabel: "Height (cm)",
      weightLabel: "Weight (kg)",
      ageLabel: "Age",
      processing: "Processing...",
      submit: "Virtual try-on",
      cancelCamera: "Cancel",
      timerOff: "Off",
      step1: "Analyzing your photo...",
      step2: "Fitting the garment on you...",
      step3: "Calculating body measurements...",
      step4: "Finding your best size...",
      step5: "Preparing your result...",
      heightWeightRequired: "Enter your height and weight — they're required for reliable size advice.",
      fitPreferenceLabel: "Fit preference",
      fitSlim: "Slim",
      fitRegular: "Regular",
      fitComfort: "Relaxed",
      sidePhotoLabel: "Side-view photo (optional)",
      sidePhotoHelp: "An extra photo from the side makes chest and waist estimates much more accurate.",
      addSidePhotoCamera: "Take side photo",
      addSidePhotoUpload: "Upload side photo",
      removeSidePhoto: "Remove side photo",
      cameraGuideSide: "Turn 90° — stand with your side to the camera",
      yourSizes: "Your size advice",
      alternativeSizeHint: "Close call — size {alt} may also fit",
      privacyConsent: "I consent to my photo and details being processed for the virtual try-on. Photos are deleted automatically.",
      privacyPolicyLink: "Privacy policy",
      privacyConsentRequired: "Please consent to your photo being processed first.",
      fitBuildAthletic: "Athletic build detected (chest-waist difference {drop} cm)",
      fitBuildFuller: "Fuller waist detected (chest-waist difference {drop} cm)",
      fitTipAthleticJacket: "With your build, size {size} fits the shoulders; a tailor can easily take in the waist (±15-25 each). Size {alt} is roomier but will bag at the waist.",
      fitTipAthleticJacketOption: "Size {size} follows the size chart. If it feels roomy at the waist for your athletic build, {alt} + having the waist taken in is an excellent option — shoulders are decisive.",
      fitTipFullerJacket: "Size {size} gives comfort at the waist. If the shoulders feel too big, letting out {alt} at the waist usually isn't possible — better to size up.",
      fitTipBetweenJacket: "You're between two sizes. {size} = tailored silhouette, {alt} = more room to move. Shoulders are decisive — a tailor can't alter those.",
      fitTipBetweenTrousers: "Between sizes? Pick {size} — a tailor can take in or let out a waistband by up to ±4 cm.",
      fitTipTrousersAlter: "Tip: a tailor can adjust a trouser waist by up to ±4 cm; the length almost always.",
      fitTipBetweenGilet: "Waistcoat {size} fits close; {alt} is looser. A waistcoat should fit snug — the back strap allows fine-tuning.",
      adjustedByHistory: "Based on your previous purchases or exchanges with this brand",
      adjustedByBrandUp: "This brand runs small — we recommend one size up",
      adjustedByBrandDown: "This brand runs large — we recommend one size down",
      adjustedByFit: "Adjusted to your fit preference",
    },

    showroom: {
      title: "Virtual Showroom",
      subtitle: "Swipe through the collection and pick what you want to try on",
      tryThis: "Try this on",
      invalidLink: "Invalid link.",
      loadFailed: "Could not load the showroom. Please try again later.",
      empty: "No items are available for virtual try-on yet.",
      viewSwipe: "Swipe",
      viewGrid: "Overview",
      itemCount: "{count} items",
      viewProduct: "View in store",
    },

    landing: {
      invalidLink: "Invalid link.",
      notFound: "This page was not found or has expired.",
      pageNotFound: "Page not found",
      calcFailed: "Could not calculate size advice.",
      defaultTitle: "Your virtual look – {product}",
      defaultIntro: "Discover below how this item looks on you, specially composed by AI.",
      yourLook: "Your look",
      styleAdvice: "Style advice",
      originalPhotos: "Original photos",
      yourPhotoAndArticle: "Your photo & the item",
      yourPhoto: "Your photo",
      sizeAdvice: "Size advice",
      sizeCalculator: "Size calculator",
      sizeCalcHelp: "Enter your details for a personal size recommendation.",
      age: "Age",
      heightCm: "Height (cm)",
      weightKg: "Weight (kg)",
      calculating: "Calculating...",
      calculateMySize: "Calculate my size",
      sizeResult: "Size {size}",
      extraPhotos: "Extra photos",
      orderOutfit: "Order outfit",
      dashboardPromo: "Your virtual fitting room",
      dashboardPromoText: "Create a free account to save and review all your virtual try-on sessions.",
      viewOutfits: "View my outfits →",
      recommendedSize: "Recommended size",
      recommendedSizes: "Recommended sizes",
      colbert: "Jacket",
      gilet: "Waistcoat",
      pantalon: "Trousers",
      overhemd: "Shirt",
      overall: "Size",
      bodyMeasurements: "Your estimated body measurements",
      chest: "Chest",
      waist: "Waist",
      hips: "Hips",
      shoulders: "Shoulders",
      armLength: "Arm length",
      inseam: "Inseam",
      neck: "Neck",
      cm: "cm",
      confidence: "Confidence",
      confidenceLow: "low",
      confidenceMedium: "medium",
      confidenceHigh: "high",
    },

    sessionDetail: {
      backToOverview: "← Back to overview",
      notFound: "Not found",
      notFoundDesc: "This session does not exist.",
      yourLook: "Your look",
      styleAdvice: "Style advice",
      originalPhotos: "Original photos",
      yourPhotoAndArticle: "Your photo & the item",
      orderOutfit: "Order outfit",
    },

    dashboard: {
      virtualFittingRoom: "Your virtual fitting room",
      welcomeBack: "Welcome back",
      createAccount: "Create account",
      namePlaceholder: "Name (optional)",
      emailPlaceholder: "Email address",
      passwordPlaceholder: "Password",
      privacyLabel: "I agree to the <strong>privacy policy</strong> and understand how my data is processed.",
      privacyRequired: "*",
      marketingLabel: "Yes, I'd like to receive news and offers by email (optional).",
      privacyError: "You must accept the privacy policy to create an account.",
      pleaseWait: "Please wait...",
      login: "Log in",
      noAccount: "No account yet? Register here →",
      hasAccount: "← Already have an account? Log in",
      logout: "Log out",
      myFittingRoom: "My Virtual Fitting Room",
      outfitsGenerated: "{count} outfit(s) generated",
      viewOutfits: "View all your generated outfits",
      noOutfits: "No outfits yet",
      noOutfitsDesc: "Use the virtual fitting room on a webshop to generate your first outfit.",
      noImage: "No image",
      unknownProduct: "Unknown product",
      aiGenerated: "AI generated",
    },

    api: {
      emailPasswordRequired: "Email and password are required.",
      passwordMinLength: "Password must be at least 6 characters.",
      privacyRequired: "You must accept the privacy policy to create an account.",
      emailExists: "An account with this email already exists.",
      registerFailed: "Something went wrong during registration.",
      invalidCredentials: "Invalid login credentials.",
      loginFailed: "Something went wrong during login.",
      accountNotFound: "Account not found.",
      sessionNotFound: "Session not found.",
      genericError: "Something went wrong.",
      unknownProduct: "Unknown product",
      shopNotConnected: "Store not connected. Go to Integration to connect with Shopify.",
      tokenExpired: "Access token expired. Reconnect the store.",
      cannotFetchProducts: "Could not fetch products from Shopify",
      shopAndProductRequired: "shop and productId are required",
      tryOnNotAvailable: "Virtual try-on is not available for this product.",
      allFieldsRequired: "shop, productId, customerEmail, and customerPhotoB64 are required",
      photoTooLarge: "The photo is too large. Try a smaller photo or take a new one.",
      invalidEmail: "Invalid email address",
      processingError: "Something went wrong during processing",
      customerNotFound: "Customer not found.",
      cannotFetchCustomers: "Could not fetch customers.",
      cannotFetchCustomer: "Could not fetch customer.",
      cannotDeleteCustomer: "Could not delete customer.",
      cannotExportCustomers: "Could not export customers.",
      cannotToggleConsent: "Could not change consent.",
      cannotFetchBranding: "Could not fetch branding.",
      cannotSaveBranding: "Could not save branding.",
      defaultOrderButton: "Complete order",
      sizeAdvice: "Based on your measurements, we recommend size {size}.",
    },

    emailTemplate: {
      subject: "Your virtual fitting room – {product}",
      heading: "Your virtual fitting room is ready!",
      greeting: "Dear customer,",
      body: "Below you will find the result of your virtual fitting room session.",
      viewResult: "View your result",
      sendFailed: "Failed to send email",
    },
  },

  // ─── GERMAN ──────────────────────────────────
  de: {
    nav: {
      appName: "ZyncFit",
      integration: "Integration",
      products: "Produkte & Kleidungsfotos",
      sessions: "Anprobe-Sitzungen",
      analytics: "Conversion & Analytics",
      customers: "Kunden",
      branding: "Branding",
      aiSettings: "KI-Einstellungen",
      settings: "Einstellungen",
      about: "Über",
      poweredBy: "Powered by ZyncFit",
      activeShop: "Aktiver Shop",
      logout: "Abmelden",
      landingPageTemplate: "Landingpage",
      sizeCharts: "Größentabellen",
      showroom: "Showroom",
    },

    showroomAdmin: {
      title: "Virtueller Showroom",
      subtitle: "Teilen Sie diesen Link oder fügen Sie ihn Ihrem Shop-Menü hinzu — Kunden sehen alle Artikel, die virtuell anprobiert werden können, und können durch sie wischen.",
      linkLabel: "Showroom-Link",
      copy: "Link kopieren",
      copied: "Kopiert!",
      open: "Showroom ansehen",
      menuTitle: "Zu Ihrem Shop-Menü hinzufügen",
      menuStep1: "Gehen Sie in Shopify zu Online Store → Navigation",
      menuStep2: "Wählen Sie Ihr Hauptmenü und klicken Sie auf 'Add menu item'",
      menuStep3: "Fügen Sie den Showroom-Link als Ziel ein und benennen Sie den Eintrag, z. B. 'Virtuell anprobieren'",
      productsAvailable: "{count} Artikel im Showroom verfügbar",
      noProducts: "Noch keine Artikel verfügbar — laden Sie zuerst Kleidungsfotos unter Produkte & Anprobefotos hoch.",
      loadFailed: "Showroom-Vorschau konnte nicht geladen werden.",
    },

    analytics: {
      title: "Conversion & Analytics",
      subtitle: "Welche Anprobe-Kunden bestellt haben und was retourniert wurde",
      refresh: "Bestellungen aktualisieren",
      refreshing: "Abgleich läuft...",
      moreToCheck: "Noch {count} Kunden zu prüfen — erneut auf 'Bestellungen aktualisieren' klicken.",
      kpiSessions: "Anprobe-Sitzungen",
      kpiCustomers: "Einzigartige Kunden",
      kpiOrdered: "Bestellt",
      kpiOrderedSub: "{count} Kunden",
      kpiReturned: "Retourniert",
      kpiReturnedSub: "{count} der Käufer",
      kpiPending: "Noch nicht geprüft",
      colDate: "Datum",
      colCustomer: "Kunde",
      colProduct: "Produkt",
      colInput: "Eingegeben",
      colAdvice: "Größenempfehlung",
      colOrder: "Bestellung",
      colReturned: "Retoure",
      returnedYes: "Retourniert",
      returnedNo: "Behalten",
      notChecked: "Noch nicht geprüft",
      yearsShort: "J.",
      aiEstimate: "KI-Schätzung Brust/Taille",
      noData: "Noch keine Anprobe-Sitzungen",
      noDataText: "Sobald Kunden die Anprobe nutzen, erscheinen hier die Conversion-Daten.",
    },

    common: {
      save: "Speichern",
      saving: "Speichern...",
      cancel: "Abbrechen",
      delete: "Löschen",
      edit: "Bearbeiten",
      loading: "Laden...",
      search: "Suchen",
      all: "Alle",
      yes: "Ja",
      no: "Nein",
      back: "Zurück",
      retry: "Erneut versuchen",
      close: "Schließen",
      error: "Etwas ist schiefgelaufen",
      success: "Erfolg!",
      poweredBy: "Powered by ZyncFit",
      or: "oder",
      status: "Status",
      date: "Datum",
      actions: "Aktionen",
      email: "E-Mail-Adresse",
      name: "Name",
      password: "Passwort",
    },

    settings: {
      title: "Einstellungen",
      subtitle: "Verwalte die Sprache und allgemeine Einstellungen deiner ZyncFit-App.",
      language: "Sprache",
      languageHelp: "Spracheinstellungen für das Dashboard und für Kunden.",
      dashboardLanguage: "Dashboard-Sprache",
      dashboardLanguageHelp: "Die Sprache des Admin-Dashboards (für Sie als Verwalter).",
      customerLanguage: "Kundensprache",
      customerLanguageHelp: "Die Sprache für kundenorientierte Inhalte: E-Mails, Widget, Anprobeseite.",
      sizeAdviceTitle: "Größenberatung",
      sizeAdviceDescription: "Legen Sie fest, ob Kunden beim Anprobieren eine Größenempfehlung sehen.",
      sizeAdviceToggle: "Größenberatung für Kunden anzeigen",
      sizeAdviceToggleHelp: "Wenn deaktiviert, sehen Kunden nur das Anprobefoto — ohne Größenberatung oder Größe/Gewicht-Felder.",
      sizeAdviceOffWarning: "Größenberatung ist deaktiviert. Kunden sehen nur das virtuelle Anprobefoto. Auch bestehende Anprobeseiten zeigen keine Größenberatung mehr.",
      saved: "Einstellungen gespeichert!",
      saveFailed: "Speichern fehlgeschlagen",
      shopLabel: "Shop:",
    },

    login: {
      subtitle: "Admin-Dashboard",
      emailPlaceholder: "admin@beispiel.de",
      passwordPlaceholder: "Passwort",
      loginButton: "Anmelden",
      loggingIn: "Anmeldung läuft...",
      fieldsRequired: "Bitte E-Mail und Passwort eingeben.",
      setupTitle: "Erstes Admin-Konto erstellen",
      namePlaceholder: "Dein Name",
      setupPasswordPlaceholder: "Wähle ein starkes Passwort",
      passwordRequirements: "Min. 8 Zeichen, mit Großbuchstabe, Kleinbuchstabe und Zahl.",
      createAccount: "Konto erstellen",
      setupWithShopify: "Mit Shopify einrichten",
      shopifyLoginTitle: "Mit Shopify anmelden",
      shopDomain: "Shopify-Shop-Domain",
      continueWithShopify: "Weiter mit Shopify",
      backToLogin: "Zurück zur Anmeldung",
      loginWithShopify: "Mit Shopify anmelden",
      enterShopDomain: "Bitte gib deine Shop-Domain ein.",
      shopifyFailed: "Shopify-Anmeldung fehlgeschlagen. Bitte erneut versuchen.",
    },

    integration: {
      title: "Integration",
      shopifyConnection: "Shopify-Verbindung",
      connectionDesc: "Verbinde deinen Shopify-Shop, um Produkte zu importieren und die virtuelle Anprobe zu aktivieren.",
      connectedShops: "Verbundene Shops",
      connectedShopsDesc: "Unten siehst du alle verbundenen Shopify-Shops. Wechsle zwischen Shops, um deren Produkte und Einstellungen zu verwalten.",
      addShop: "Neuen Shop verbinden",
      active: "Aktiv",
      switchTo: "Auswählen",
      checkingStatus: "Status wird überprüft...",
      connected: "Verbunden",
      notConnected: "Nicht verbunden",
      permissions: "Berechtigungen:",
      connectedOn: "Verbunden am:",
      disconnect: "Trennen",
      reconnect: "Erneut verbinden",
      shopDomainLabel: "Shopify-Shop-Domain",
      shopDomainPlaceholder: "deinshop.myshopify.com",
      shopDomainHelp: "Gib die .myshopify.com-Domain deines Shops ein.",
      installViaAppStore: "Installiere ZyncFit über den Shopify App Store oder über den Installationslink in deinem Shopify-Admin. Nach der Installation erscheint dein Shop hier automatisch.",
      installViaAppStoreHelp: "Öffne die App aus deinem Shopify-Admin (Apps → ZyncFit), um die Verbindung zu starten. Keine manuelle Eingabe der Shop-Domain nötig.",
      connectButton: "Mit Shopify verbinden",
      connectedSuccess: "Shopify-Shop erfolgreich verbunden!",
      enterDomain: "Bitte gib eine Shop-Domain ein",
      confirmDisconnect: "Bist du sicher, dass du die Verbindung trennen möchtest?",
      disconnected: "Shop getrennt",
      howItWorks: "Wie funktioniert es?",
      step1: "Verbinde deinen Shopify-Shop oben.",
      step2: "Gehe zu Produkte & Kleidungsfotos, um Kleidungsfotos hochzuladen.",
      step3: "Der Virtuelle-Anprobe-Button erscheint automatisch auf deinen Produktseiten.",
      step4: "Kunden machen ein Foto und erhalten ihr KI-generiertes Ergebnis per E-Mail.",
      step5: "Sieh dir Sitzungen und Ergebnisse im Dashboard an.",
      widgetTitle: "Webshop-Widget installieren",
      widgetDesc: 'Füge den "Virtuelle Anprobe"-Button zu deinen Produktseiten hinzu. Der Button erscheint nur bei Produkten, für die du ein Kleidungsfoto eingestellt hast.',
      widgetTip: "Das Widget findet automatisch die richtige Position auf der Produktseite. Falls das nicht gut funktioniert, verwende den Custom Liquid Block oben.",
      widgetOption1Title: "Option 1: Über den Theme Editor (empfohlen)",
      widgetOption1Step1: "Gehe zu Online Store → Themes → Customize",
      widgetOption1Step2: "Öffne eine Produktseite im Editor",
      widgetOption1Step3: "Klicke auf 'Block hinzufügen' wo du den Button platzieren möchtest",
      widgetOption1Step4: "Wähle 'Custom Liquid'",
      widgetOption1Step5: "Füge den folgenden Code ein:",
      widgetOption1Hint: "Du kannst den Block an die gewünschte Position auf der Produktseite ziehen.",
      widgetOption2Title: "Option 2: Automatisch auf allen Seiten",
      widgetOption2Desc: "Füge dieses Script zu Online Store → Themes → Edit code → layout/theme.liquid hinzu (vor </body>):",
      widgetOption2Hint: "Das Script erkennt automatisch Produktseiten und platziert den Button unter dem 'In den Warenkorb'-Button.",
      showroomTitle: "Virtueller Showroom — Link für Ihr Menü",
      showroomDesc: "Fügen Sie diesen Link zu Ihrer Shop-Navigation hinzu (Online Store → Navigation). Kunden sehen dann alle Artikel, die virtuell anprobiert werden können, und können durch sie wischen.",
      showroomCopy: "Link kopieren",
      showroomCopied: "Kopiert!",
    },

    products: {
      title: "Produkte & Kleidungsfotos",
      description: "Lade ein Kleidungsfoto für jedes Produkt hoch. Die KI verwendet dieses Foto, um dem Kunden das Kleidungsstück virtuell anzuprobieren.",
      searchPlaceholder: "Produkte suchen...",
      sort: "Sortieren",
      type: "Typ",
      notConnected: "Shopify-Shop nicht verbunden",
      notConnectedDesc: "Verbinde zuerst deinen Shopify-Shop über die Integrationsseite.",
      goToIntegration: "Zur Integration",
      noProducts: "Keine Produkte gefunden",
      noProductsDesc: "Keine Produkte entsprechen deinen Filtern.",
      clearFilters: "Filter löschen",
      ofProducts: "von {total} Produkten",
      searchResults: "Suchergebnisse",
      changePhoto: "Foto ändern",
      uploadPhoto: "Foto hochladen",
      landingPage: "Landingpage",
      deletePhoto: "Foto löschen",
      photoSaved: "Kleidungsfoto für {name} gespeichert!",
      confirmDeletePhoto: "Bist du sicher, dass du das Kleidungsfoto löschen möchtest?",
      photoDeleted: "Kleidungsfoto gelöscht",
      chooseOrUpload: "Wähle ein vorhandenes Foto oder lade ein neues hoch",
      sortNewest: "Neueste zuerst",
      sortOldest: "Älteste zuerst",
      sortAZ: "A → Z",
      sortZA: "Z → A",
      sortPriceLow: "Preis: niedrig → hoch",
      sortPriceHigh: "Preis: hoch → niedrig",
      tabAll: "Alle",
      tabWithPhoto: "Mit Foto",
      tabWithoutPhoto: "Ohne Foto",
      tabActive: "Aktiv",
      tabDraft: "Entwurf",
      products: "Produkte",
    },

    sessions: {
      title: "Anprobe-Sitzungen",
      description: "Übersicht aller virtuellen Anprobe-Sitzungen. Kunden, die die Anprobe genutzt haben, erscheinen hier mit ihrem Ergebnis.",
      noSessions: "Noch keine Sitzungen",
      noSessionsDesc: "Wenn Kunden die virtuelle Anprobe nutzen, erscheinen ihre Sitzungen hier.",
      emailCol: "E-Mail-Adresse",
      productIdCol: "Produkt-ID",
      dateCol: "Datum",
      emailStatus: "E-Mail",
      landingPageCol: "Landingpage",
      sent: "Gesendet",
      notSent: "Nicht gesendet",
    },

    customers: {
      title: "Kunden",
      subtitle: "Verwalte Kundenkonten, Marketing-Einwilligung und Datenschutz (DSGVO).",
      exportMarketing: "Marketing-Liste exportieren",
      exportAll: "Alle exportieren",
      exportFailed: "Export fehlgeschlagen",
      marketingExported: "Marketing-Kunden exportiert!",
      allExported: "Alle Kunden exportiert!",
      deleted: "Kunde gelöscht (DSGVO Recht auf Löschung).",
      optIn: "Opt-in",
      noConsent: "Keine Einwilligung",
      accepted: "Akzeptiert",
      notAccepted: "Nicht akzeptiert",
      marketingOff: "Marketing aus",
      marketingOn: "Marketing an",
      gdprBanner: "Diese Seite ist DSGVO-konform. Kundendaten werden gemäß der Datenschutzrichtlinie verarbeitet. Kunden können über das Recht auf Löschung entfernt werden.",
      searchPlaceholder: "Nach E-Mail oder Name suchen...",
      filterAll: "Alle",
      filterOptIn: "Marketing Opt-in",
      noCustomers: "Noch keine Kunden",
      noCustomersDesc: "Wenn Kunden über die virtuelle Anprobe ein Konto erstellen, erscheinen sie hier.",
      sessionsCol: "Sitzungen",
      marketingCol: "Marketing",
      privacyCol: "Datenschutz",
      registeredCol: "Registriert",
      totalCustomers: "Kunden gesamt",
      marketingOptIn: "Marketing Opt-in",
      deleteTitle: "Kunden löschen?",
      deleteConfirm: "Endgültig löschen",
      deleteWarning: "Dies löscht dauerhaft alle Daten dieses Kunden einschließlich Sitzungen, gemäß DSGVO Recht auf Löschung. Dies kann nicht rückgängig gemacht werden.",
      detailsTitle: "Kundendetails",
      noName: "Kein Name",
      notSynced: "Nicht synchronisiert",
      csvHeader: "E-Mail,Name,Telefon,Marketing-Einwilligung,Einwilligungsdatum,Registriert",
    },

    branding: {
      title: "Branding & Stil",
      subtitle: "Passe das Erscheinungsbild der Kundenseiten an (Landing, Dashboard, virtuelle Anprobe).",
      saved: "Branding gespeichert!",
      storeDetails: "Shop-Details",
      storeName: "Shopname",
      storeNamePlaceholder: "Dein Shop",
      logoUrl: "Logo-URL",
      logoHelp: "Link zu deinem Logo (z.B. aus Shopify Files)",
      colors: "Farben",
      primaryColor: "Primärfarbe",
      secondaryColor: "Sekundärfarbe",
      accentColor: "Akzentfarbe",
      backgroundColor: "Hintergrundfarbe",
      textColor: "Textfarbe",
      style: "Stil",
      fontFamily: "Schriftart",
      borderRadius: "Eckenrundung",
      buttonStyle: "Button-Stil",
      preview: "Vorschau",
      previewDesc: "So sieht der Kunde deine Anprobe",
      radiusSharp: "Scharf",
      radiusSlight: "Leicht",
      radiusRounded: "Abgerundet",
      radiusRound: "Rund",
      radiusPill: "Pill",
      previewResult: "So sieht das Kleidungsstück an dir aus",
      previewButton: "Outfit bestellen",
      previewStoreName: "Dein Shop",
      previewSubtitle: "Virtuelle Anprobe",
      showImageBadge: "'Virtuell Anprobieren' Label auf Produktbild anzeigen",
      showImageBadgeHelp: "Zeigt ein kleines Label am unteren Rand des Produktbilds auf Produktseiten an, damit Kunden wissen, dass eine virtuelle Anprobe verfügbar ist.",
    },

    sizeChart: {
      title: "Größentabelle",
      description: "Geben Sie die Maße in Zentimetern pro Größe ein. Die KI verwendet diese Tabelle, um die richtige Größe zu empfehlen.",
      sizeLabel: "Größe",
      addSize: "+ Größe hinzufügen",
      saved: "Größentabelle gespeichert!",
      chest: "Brust",
      waist: "Taille",
      hips: "Hüfte",
      shoulders: "Schultern",
      armLength: "Armlänge",
      inseam: "Schrittlänge",
      neck: "Hals",
      pageTitle: "Größentabellen pro Marke",
      pageDescription: "Legen Sie pro Marke eine Standard-Größentabelle fest. Alle Produkte dieser Marke erben diese Maße automatisch. Sie können pro Produkt über Produkte & Kleidungsfotos feinabstimmen.",
      noVendors: "Keine Marken gefunden. Produkte werden aus Shopify geladen.",
      sizes: "Größen",
      noChart: "Keine Größentabelle",
      editChart: "Bearbeiten",
      addChart: "Größentabelle hinzufügen",
      vendorDescription: "Diese Maße gelten für alle Produkte dieser Marke, es sei denn, Sie legen pro Produkt eine eigene Größentabelle fest.",
      inheritedFrom: "Diese Maße stammen von der Marke",
      inheritedHint: "Passen Sie sie an, um eine eigene Größentabelle für dieses Produkt zu speichern.",
      scanImage: "📷 Bild scannen",
      scanning: "Analysieren...",
      scanSuccess: "KI hat {count} Größen aus dem Bild erkannt. Überprüfen Sie die Werte und speichern Sie.",
      scanFailed: "Scan fehlgeschlagen",
      import: "Importieren",
      importButton: "Importieren & konvertieren",
      scrapeUrl: "🌐 URL scrapen",
      scrapeButton: "Scrapen & konvertieren",
      scraping: "Seite wird analysiert...",
      scrapeDescription: "Fügen Sie die URL der Size-Guide-Seite der Marke ein. Die KI liest die Größentabellen, rechnet Inches und UK-Größen in cm/EU um und konvertiert Kleidungsmaße in Körpermaße. Sie erhalten zuerst eine Vorschau — gespeichert wird von Ihnen.",
      scrapeSuccess: "KI hat {count} Größen von der Seite extrahiert. Überprüfen Sie die Werte und speichern Sie.",
      scrapeFailed: "Scraping fehlgeschlagen",
    },

    ai: {
      title: "KI-Einstellungen",
      subtitle: "Konfiguriere die Gemini-KI-Prompts für die virtuelle Anprobe.",
      saved: "Einstellungen gespeichert!",
      loadFailed: "KI-Einstellungen konnten nicht geladen werden",
      saveFailed: "Speichern fehlgeschlagen",
      model: "Gemini-Modell",
      modelDesc: "Modell für Stilberatung-Text",
      modelWarning: "**Hinweis:** Das Modell für die Bildgenerierung ist immer `gemini-2.0-flash-preview-image-generation`. Das obige Modell wird nur für den Stilberatung-Text verwendet.",
      imagePrompt: "Prompt für Bildgenerierung",
      imagePromptReset: "Standard wiederherstellen",
      imagePromptDesc: "Dieser Prompt wird zusammen mit dem Kleidungsfoto und Kundenfoto an Gemini gesendet. Das Ergebnis ist ein KI-generiertes Bild.",
      imagePromptTip: "**Tipp:** Verwende `{garment}` und `{customer}` als Platzhalter für das Kleidungsfoto und Kundenfoto im Prompt.",
      stylePrompt: "Prompt für Stilberatung (Text)",
      stylePromptReset: "Standard wiederherstellen",
      stylePromptDesc: "Dieser Prompt generiert die textliche Stilberatung, die beim Ergebnis angezeigt wird.",
      recommended: "empfohlen",
      newest: "neueste",
      premium: "premium",
      textOnly: "nur Stilberatung-Text",
    },

    landingEditor: {
      title: "Landingpage bearbeiten — {name}",
      backAction: "Produkte",
      saving: "Speichern...",
      saved: "Landingpage gespeichert!",
      titleSection: "Seitentitel & Einleitung",
      pageTitle: "Seitentitel",
      pageTitlePlaceholder: "Dein virtueller Look",
      introText: "Einleitungstext",
      introPlaceholder: "Entdecke, wie dieses Kleidungsstück an dir aussieht...",
      sizeSection: "Größenberatung & Passformtext",
      showCalculator: "Größenrechner auf der Landingpage anzeigen",
      sizeGuide: "Passformberatung-Text",
      sizeGuidePlaceholder: "Informationen über Größen und Passform...",
      extraSection: "Zusätzlicher Text & Bilder",
      additionalText: "Zusätzlicher Text",
      additionalTextPlaceholder: "Freier Text für zusätzliche Informationen...",
      extraImages: "Zusätzliche Bilder",
      addImage: "Bild hinzufügen",
      dropImage: "oder hier ablegen",
      orderSection: "Bestell-Button",
      orderButtonText: "Button-Text",
      orderButtonTextPlaceholder: "Bestellung abschließen",
      orderButtonUrl: "Button-URL (optional)",
      orderButtonUrlHelp: "Falls leer, wird die Shopify-Produkt-URL verwendet.",
    },

    landingTemplate: {
      title: "Landingpage-Vorlage",
      saved: "Landingpage-Vorlage gespeichert!",
      infoBanner: "Dies ist die Standard-Landingpage, die für alle Produkte verwendet wird. Wenn du bei einem bestimmten Produkt eine Landingpage einrichtest, wird diese stattdessen verwendet.",
      titleSection: "Seitentitel & Einleitung",
      pageTitle: "Seitentitel",
      pageTitlePlaceholder: "Dein virtueller Look",
      pageTitleHelp: "Wird oben auf jeder Landingpage angezeigt.",
      introText: "Einleitungstext",
      introPlaceholder: "Entdecke, wie dieses Kleidungsstück an dir aussieht...",
      introHelp: "Wird unter den Fotos auf allen Landingpages angezeigt.",
      sizeSection: "Größenberatung & Passformtext",
      showCalculator: "Größenrechner auf der Landingpage anzeigen",
      sizeGuide: "Passformberatung-Text",
      sizeGuidePlaceholder: "Informationen über Größen und Passform...",
      extraSection: "Zusätzlicher Text & Bilder",
      additionalText: "Zusätzlicher Text",
      additionalTextPlaceholder: "Freier Text für zusätzliche Informationen...",
      extraImages: "Zusätzliche Bilder",
      addImage: "Bild hinzufügen",
      dropImage: "oder hier ablegen",
      orderSection: "Bestell-Button",
      orderButtonText: "Button-Text",
      orderButtonTextPlaceholder: "Bestellung abschließen",
      orderButtonUrl: "Button-URL (optional)",
      orderButtonUrlHelp: "Falls leer, wird die Shopify-Produkt-URL verwendet.",
    },

    tryOn: {
      title: "Virtuelle Anprobe",
      invalidLink: "Ungültiger Link.",
      loadFailed: "Produkt konnte nicht geladen werden",
      cameraNotAvailable: "Kamera nicht verfügbar. Verwende 'Foto hochladen' unten.",
      takePhotoFirst: "Bitte mache zuerst ein Foto von dir.",
      enterEmail: "Bitte gib deine E-Mail-Adresse ein.",
      photoTooBig: "Das Foto ist zu groß. Versuche ein kleineres Foto hochzuladen.",
      serverUnreachable: "Server nicht erreichbar. Überprüfe deine Internetverbindung und versuche es erneut.",
      loading: "Laden...",
      success: "Geschafft!",
      resultSent: "Dein virtuelles Anprobe-Ergebnis ist unterwegs an",
      styleAdvice: "Stilberatung",
      imageTemporarilyUnavailable: "Das AI-Bild konnte aufgrund hoher Servernachfrage nicht erstellt werden. Ihre Stilberatung ist verfügbar. Das Ergebnis wurde auch per E-Mail gesendet.",
      emailFailed: "Die E-Mail konnte nicht gesendet werden. Nutzen Sie den Link unten, um Ihr Ergebnis anzuzeigen.",
      openLandingDirect: "Ergebnis ansehen →",
      backToShop: "Zurück zum Shop",
      thisArticle: "Dieses Produkt",
      yourPhoto: "Dein Foto",
      chooseOtherPhoto: "Anderes Foto wählen",
      takePhotoLabel: "Mache ein Foto von dir",
      takePhotoHelp: "Ein Ganzkörperfoto in anliegender Kleidung ergibt die beste Größenberatung.",
      photoTip1: "Stehe aufrecht mit den Armen leicht vom Körper entfernt",
      photoTip2: "Achte darauf, dass dein ganzer Körper sichtbar ist (Kopf bis Fuß)",
      photoTip3: "Lass jemand anderen das Foto aus ca. 2 Metern Entfernung machen",
      photoTip4: "Trage eng anliegende Kleidung (keine dicke Jacke oder Pullover)",
      photoTip5: "Sorge für gute Beleuchtung und einen ruhigen Hintergrund",
      photoTip6: "Stehe mit den Füßen schulterbreit auseinander",
      openCamera: "Kamera öffnen",
      cameraGuideSelf: "Richte deinen Körper an der Silhouette aus — Kopf bis Fuß sichtbar",
      cameraGuideOther: "Richte auf die ganze Person — Kopf bis Fuß in der Silhouette",
      distanceTip: "Silhouette ausfüllen — ca. 2 Meter Abstand",
      guideShoulders: "Schulter",
      guideChest: "Brust",
      guideWaist: "Taille",
      guideHips: "Hüfte",
      uploadPhoto: "Foto hochladen",
      emailLabel: "E-Mail-Adresse",
      emailPlaceholder: "deine@email.de",
      emailHelp: "Wir senden das Ergebnis an diese Adresse",
      bodyInfoLabel: "Deine Angaben",
      bodyInfoHelp: "Gib deine Größe, Gewicht und Alter ein für eine genauere Größenberechnung.",
      heightLabel: "Größe (cm)",
      weightLabel: "Gewicht (kg)",
      ageLabel: "Alter",
      processing: "Wird verarbeitet...",
      submit: "Virtuelle Anprobe",
      cancelCamera: "Abbrechen",
      timerOff: "Aus",
      step1: "Dein Foto wird analysiert...",
      step2: "Das Kleidungsstück wird angepasst...",
      step3: "Körpermaße werden berechnet...",
      step4: "Beste Größe wird ermittelt...",
      step5: "Ergebnis wird vorbereitet...",
      heightWeightRequired: "Gib deine Größe und dein Gewicht ein — sie sind für eine zuverlässige Größenempfehlung erforderlich.",
      fitPreferenceLabel: "Passform-Präferenz",
      fitSlim: "Schmal",
      fitRegular: "Normal",
      fitComfort: "Locker",
      sidePhotoLabel: "Seitenfoto (optional)",
      sidePhotoHelp: "Ein zusätzliches Foto von der Seite macht die Schätzung von Brust- und Taillenumfang deutlich genauer.",
      addSidePhotoCamera: "Seitenfoto aufnehmen",
      addSidePhotoUpload: "Seitenfoto hochladen",
      removeSidePhoto: "Seitenfoto entfernen",
      cameraGuideSide: "Drehe dich um 90° — stelle dich seitlich zur Kamera",
      yourSizes: "Deine Größenempfehlung",
      alternativeSizeHint: "Knapp — Größe {alt} könnte auch passen",
      privacyConsent: "Ich stimme der Verarbeitung meines Fotos und meiner Daten für die virtuelle Anprobe zu. Fotos werden automatisch gelöscht.",
      privacyPolicyLink: "Datenschutzerklärung",
      privacyConsentRequired: "Bitte stimme zuerst der Verarbeitung deines Fotos zu.",
      fitBuildAthletic: "Athletischer Körperbau erkannt (Brust-Taille-Differenz {drop} cm)",
      fitBuildFuller: "Kräftigere Taille erkannt (Brust-Taille-Differenz {drop} cm)",
      fitTipAthleticJacket: "Bei Ihrem Körperbau passt Größe {size} an den Schultern; ein Schneider kann die Taille leicht enger machen (±15-25 pro Stück). Größe {alt} sitzt weiter, wirft aber Falten an der Taille.",
      fitTipAthleticJacketOption: "Größe {size} folgt der Größentabelle. Sitzt sie bei Ihrem athletischen Körperbau zu locker an der Taille, ist {alt} + Taille enger nähen lassen eine ausgezeichnete Option — die Schultern sind entscheidend.",
      fitTipFullerJacket: "Größe {size} bietet Komfort an der Taille. Sitzen die Schultern zu weit, lässt sich {alt} an der Taille meist nicht erweitern — lieber größer wählen.",
      fitTipBetweenJacket: "Sie liegen zwischen zwei Größen. {size} = taillierte Silhouette, {alt} = mehr Bewegungsfreiheit. Die Schultern sind entscheidend — die kann ein Schneider nicht ändern.",
      fitTipBetweenTrousers: "Zwischen zwei Größen? Wählen Sie {size} — ein Hosenbund kann bis zu ±4 cm enger oder weiter gemacht werden.",
      fitTipTrousersAlter: "Tipp: Ein Schneider kann den Hosenbund bis zu ±4 cm anpassen; die Länge fast immer.",
      fitTipBetweenGilet: "Weste {size} sitzt anliegend; {alt} fällt lockerer. Eine Weste sollte eng anliegen — hinten sitzt eine Schnalle zur Feinjustierung.",
      adjustedByHistory: "Basierend auf deinen früheren Käufen oder Umtauschen bei dieser Marke",
      adjustedByBrandUp: "Diese Marke fällt klein aus — wir empfehlen eine Größe größer",
      adjustedByBrandDown: "Diese Marke fällt groß aus — wir empfehlen eine Größe kleiner",
      adjustedByFit: "An deine Passform-Präferenz angepasst",
    },

    showroom: {
      title: "Virtueller Showroom",
      subtitle: "Wische durch die Kollektion und wähle, was du virtuell anprobieren möchtest",
      tryThis: "Jetzt anprobieren",
      invalidLink: "Ungültiger Link.",
      loadFailed: "Showroom konnte nicht geladen werden. Bitte später erneut versuchen.",
      empty: "Es sind noch keine Artikel für die virtuelle Anprobe verfügbar.",
      viewSwipe: "Wischen",
      viewGrid: "Übersicht",
      itemCount: "{count} Artikel",
      viewProduct: "Im Shop ansehen",
    },

    landing: {
      invalidLink: "Ungültiger Link.",
      notFound: "Diese Seite wurde nicht gefunden oder ist abgelaufen.",
      pageNotFound: "Seite nicht gefunden",
      calcFailed: "Größenberatung konnte nicht berechnet werden.",
      defaultTitle: "Dein virtueller Look – {product}",
      defaultIntro: "Entdecke unten, wie dieses Kleidungsstück an dir aussieht, speziell von KI zusammengestellt.",
      yourLook: "Dein Look",
      styleAdvice: "Stilberatung",
      originalPhotos: "Originalfotos",
      yourPhotoAndArticle: "Dein Foto & das Produkt",
      yourPhoto: "Dein Foto",
      sizeAdvice: "Größenberatung",
      sizeCalculator: "Größenrechner",
      sizeCalcHelp: "Gib deine Daten ein für eine persönliche Größenempfehlung.",
      age: "Alter",
      heightCm: "Größe (cm)",
      weightKg: "Gewicht (kg)",
      calculating: "Berechnen...",
      calculateMySize: "Meine Größe berechnen",
      sizeResult: "Größe {size}",
      extraPhotos: "Weitere Fotos",
      orderOutfit: "Outfit bestellen",
      dashboardPromo: "Deine virtuelle Anprobe",
      dashboardPromoText: "Erstelle ein kostenloses Konto, um alle deine virtuellen Anprobe-Sitzungen zu speichern und einzusehen.",
      viewOutfits: "Meine Outfits ansehen →",
      recommendedSize: "Empfohlene Größe",
      recommendedSizes: "Empfohlene Größen",
      colbert: "Sakko",
      gilet: "Weste",
      pantalon: "Hose",
      overhemd: "Hemd",
      overall: "Größe",
      bodyMeasurements: "Deine geschätzten Körpermaße",
      chest: "Brust",
      waist: "Taille",
      hips: "Hüfte",
      shoulders: "Schultern",
      armLength: "Armlänge",
      inseam: "Schrittlänge",
      neck: "Hals",
      cm: "cm",
      confidence: "Zuverlässigkeit",
      confidenceLow: "niedrig",
      confidenceMedium: "mittel",
      confidenceHigh: "hoch",
    },

    sessionDetail: {
      backToOverview: "← Zurück zur Übersicht",
      notFound: "Nicht gefunden",
      notFoundDesc: "Diese Sitzung existiert nicht.",
      yourLook: "Dein Look",
      styleAdvice: "Stilberatung",
      originalPhotos: "Originalfotos",
      yourPhotoAndArticle: "Dein Foto & das Produkt",
      orderOutfit: "Outfit bestellen",
    },

    dashboard: {
      virtualFittingRoom: "Deine virtuelle Anprobe",
      welcomeBack: "Willkommen zurück",
      createAccount: "Konto erstellen",
      namePlaceholder: "Name (optional)",
      emailPlaceholder: "E-Mail-Adresse",
      passwordPlaceholder: "Passwort",
      privacyLabel: "Ich stimme der <strong>Datenschutzrichtlinie</strong> zu und verstehe, wie meine Daten verarbeitet werden.",
      privacyRequired: "*",
      marketingLabel: "Ja, ich möchte gerne Neuigkeiten und Angebote per E-Mail erhalten (optional).",
      privacyError: "Du musst die Datenschutzrichtlinie akzeptieren, um ein Konto zu erstellen.",
      pleaseWait: "Bitte warten...",
      login: "Anmelden",
      noAccount: "Noch kein Konto? Hier registrieren →",
      hasAccount: "← Bereits ein Konto? Anmelden",
      logout: "Abmelden",
      myFittingRoom: "Meine Virtuelle Anprobe",
      outfitsGenerated: "{count} Outfit(s) generiert",
      viewOutfits: "Alle generierten Outfits ansehen",
      noOutfits: "Noch keine Outfits",
      noOutfitsDesc: "Nutze die virtuelle Anprobe in einem Webshop, um dein erstes Outfit zu generieren.",
      noImage: "Kein Bild",
      unknownProduct: "Unbekanntes Produkt",
      aiGenerated: "KI-generiert",
    },

    api: {
      emailPasswordRequired: "E-Mail und Passwort sind erforderlich.",
      passwordMinLength: "Das Passwort muss mindestens 6 Zeichen lang sein.",
      privacyRequired: "Du musst die Datenschutzrichtlinie akzeptieren, um ein Konto zu erstellen.",
      emailExists: "Es existiert bereits ein Konto mit dieser E-Mail-Adresse.",
      registerFailed: "Bei der Registrierung ist etwas schiefgelaufen.",
      invalidCredentials: "Ungültige Anmeldedaten.",
      loginFailed: "Beim Anmelden ist etwas schiefgelaufen.",
      accountNotFound: "Konto nicht gefunden.",
      sessionNotFound: "Sitzung nicht gefunden.",
      genericError: "Etwas ist schiefgelaufen.",
      unknownProduct: "Unbekanntes Produkt",
      shopNotConnected: "Shop nicht verbunden. Gehe zur Integration, um mit Shopify zu verbinden.",
      tokenExpired: "Zugriffstoken abgelaufen. Verbinde den Shop erneut.",
      cannotFetchProducts: "Produkte konnten nicht von Shopify abgerufen werden",
      shopAndProductRequired: "shop und productId sind erforderlich",
      tryOnNotAvailable: "Die virtuelle Anprobe ist für dieses Produkt nicht verfügbar.",
      allFieldsRequired: "shop, productId, customerEmail und customerPhotoB64 sind erforderlich",
      photoTooLarge: "Das Foto ist zu groß. Versuche ein kleineres Foto oder mache ein neues.",
      invalidEmail: "Ungültige E-Mail-Adresse",
      processingError: "Bei der Verarbeitung ist etwas schiefgelaufen",
      customerNotFound: "Kunde nicht gefunden.",
      cannotFetchCustomers: "Kunden konnten nicht abgerufen werden.",
      cannotFetchCustomer: "Kunde konnte nicht abgerufen werden.",
      cannotDeleteCustomer: "Kunde konnte nicht gelöscht werden.",
      cannotExportCustomers: "Kunden konnten nicht exportiert werden.",
      cannotToggleConsent: "Einwilligung konnte nicht geändert werden.",
      cannotFetchBranding: "Branding konnte nicht abgerufen werden.",
      cannotSaveBranding: "Branding konnte nicht gespeichert werden.",
      defaultOrderButton: "Bestellung abschließen",
      sizeAdvice: "Basierend auf Ihren Maßen empfehlen wir Größe {size}.",
    },

    emailTemplate: {
      subject: "Ihre virtuelle Anprobe – {product}",
      heading: "Ihre virtuelle Anprobe ist bereit!",
      greeting: "Sehr geehrter Kunde,",
      body: "Nachfolgend finden Sie das Ergebnis Ihrer virtuellen Anprobe-Sitzung.",
      viewResult: "Ergebnis ansehen",
      sendFailed: "E-Mail konnte nicht gesendet werden",
    },
  },
};

/**
 * Helper: get a translated string with optional interpolation.
 * Usage: t(translations.nl, 'landing.defaultTitle', { product: 'Shirt' })
 *        → "Jouw virtuele look – Shirt"
 */
export function t(lang, key, params = {}) {
  const keys = key.split(".");
  let val = translations[lang] || translations.nl;
  for (const k of keys) {
    val = val?.[k];
    if (val === undefined) {
      // Fallback to Dutch
      val = translations.nl;
      for (const fk of keys) val = val?.[fk];
      break;
    }
  }
  if (typeof val !== "string") return key;
  // Interpolate {param} placeholders
  return val.replace(/\{(\w+)\}/g, (_, p) => params[p] ?? `{${p}}`);
}
